import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import type { StepView } from "@/app/queries";
import { TabBar } from "@/app/TabBar";
import { CheckRow } from "@/ui/CheckRow";
import { stateMark } from "@/ui/route";
import { plural } from "@/lib/plural";
import { QuizBody } from "./Blocks";
import { GateBadge, GateCard } from "./Gate";
import { MARK_COLOR, blockOn, gateResources, lines, metaLine, nextUntickedTarget, resourceProgress, splitMistake, tasksDone, type TickFn } from "./model";
import { Primary } from "./Primary";
import { StuckBody } from "./Stuck";

type Tab = "res" | "quiz" | "mistakes" | "practice" | "stuck" | "gate";

const TAB_LABEL: Record<Tab, string> = {
  res: "ресурсы",
  quiz: "проверь себя",
  mistakes: "ошибки",
  practice: "практика",
  stuck: "застрял",
  gate: "гейт",
};

/** Экран шага при 390px (S8): вкладки, строки ≥ 44px, sticky-футер «до
 *  гейта» с CTA «продолжить шаг» — ведёт к первому неотмеченному. */
export function MobileStep({ view, tick, onSubmit, pending }: { view: StepView; tick: TickFn; onSubmit: () => void; pending: boolean }) {
  const [tab, setTab] = useState<Tab>("res");
  const [focus, setFocus] = useState<string | null>(null);
  const hasPractice = view.gate.tasks_total > 0 || view.content.practice.extra.length > 0;
  const tabs: Tab[] = [
    "res",
    ...(blockOn(view, "quiz") ? (["quiz"] as Tab[]) : []),
    ...(blockOn(view, "mistakes") ? (["mistakes"] as Tab[]) : []),
    ...(hasPractice ? (["practice"] as Tab[]) : []),
    ...(view.content.stuck.on ? (["stuck"] as Tab[]) : []),
    "gate",
  ];
  const g = view.gate;
  const pct = g.units_total > 0 ? Math.round((100 * g.units_done) / g.units_total) : 0;

  useEffect(() => {
    if (!focus) return;
    const el = document.getElementById(focus);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    setFocus(null);
  }, [focus, tab]);

  const continueStep = () => {
    const t = nextUntickedTarget(view);
    setTab(t.tab);
    setFocus(t.id);
  };

  return (
    <div className="pb-[150px]">
      <div className="flex items-center gap-2.5 border-b border-bd px-4 pb-3 pt-2">
        <Link to="/" aria-label="к маршруту" className="font-mono text-[16px] text-ink2 no-underline">
          ←
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="m-0 truncate text-[13.5px] font-semibold">{view.title}</h1>
          <div className="font-mono text-[10.5px] text-mut">
            {view.track_slug} · шаг {view.step_index} из {view.track_total} · {stateMark(view.state).label}
          </div>
        </div>
        <button type="button" onClick={() => setTab("gate")} className="cursor-pointer border-0 bg-transparent p-0 text-ink">
          <GateBadge status={view.gate_status} label={view.gate_status === "open" ? "гейт" : undefined} testId="gate-status-mobile" />
        </button>
      </div>

      <div role="tablist" aria-label="разделы шага" className="flex gap-1.5 overflow-x-auto border-b border-bd px-4 py-2.5">
        {tabs.map((t) => {
          const on = t === tab;
          return (
            <button
              key={t}
              role="tab"
              aria-selected={on}
              type="button"
              onClick={() => setTab(t)}
              className={`whitespace-nowrap rounded-[5px] border px-[11px] py-1.5 font-mono text-[11.5px] ${on ? "border-acc bg-accsoft text-acc" : "border-bd bg-transparent text-ink2"}`}
            >
              {TAB_LABEL[t]}
            </button>
          );
        })}
      </div>

      <div role="tabpanel" className="flex flex-col gap-3 px-4 pb-5 pt-3.5">
        {tab === "res" &&
          gateResources(view).map((r) => {
            const p = resourceProgress(r, view.ticks);
            const set = new Set(view.ticks.units);
            return (
              <div key={r.id} className="overflow-hidden rounded-md border border-bd bg-card">
                <div className="flex items-start gap-[9px] px-3 py-[11px]">
                  <span aria-hidden className="mt-px font-mono text-[12px]" style={{ color: MARK_COLOR[p.mark] }}>
                    {p.mark}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold leading-[1.35]">{r.title}</div>
                    <div className="mt-1 font-mono text-[10.5px] text-mut">
                      {metaLine(r)} · {p.noGuide ? "гайд не размечен" : p.frac}
                    </div>
                  </div>
                </div>
                <div className="border-t border-bd bg-bg2 px-3">
                  {p.noGuide ? (
                    <div id={`resource-${r.id}`}>
                      <CheckRow checked={p.wholeTicked} onChange={(on) => tick({ kind: "resource", key: r.id, on })} label="отметить ресурс целиком" />
                    </div>
                  ) : (
                    r.units.map((u) => (
                      <div key={u.key} id={`unit-${u.key}`} data-skip={Boolean(u.skip)} className="border-b border-bd last:border-b-0">
                        <CheckRow
                          checked={set.has(u.key)}
                          onChange={(on) => tick({ kind: "unit", key: u.key, on })}
                          label={
                            u.skip ? (
                              <span className="text-mut">
                                {u.topic}
                                <span className="mt-0.5 block font-mono text-[10.5px]">⏭️ можно пропустить · не входит в гейт</span>
                              </span>
                            ) : (
                              u.topic
                            )
                          }
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}

        {tab === "res" && <Primary view={view} tick={tick} />}

        {tab === "quiz" && (
          <div className="rounded-md border border-bd bg-card p-3" style={{ borderLeft: "2px solid var(--info)" }}>
            <QuizBody view={view} tick={tick} />
          </div>
        )}

        {tab === "mistakes" &&
          lines(view.content.blocks.mistakes?.text)
            .map(splitMistake)
            .map((m, i) => (
              <div key={i} className="rounded-md border border-bd bg-card p-3 text-pretty text-[13px] leading-[1.5] text-ink2" style={{ borderLeft: "2px solid var(--err)" }}>
                <span className="font-semibold text-ink">{m.t}</span>
                {m.d && <> — {m.d}</>}
              </div>
            ))}

        {tab === "practice" && (
          <>
            <div className="font-mono text-[11px] text-mut">
              {g.tasks_done} / {g.tasks_total} {plural(g.tasks_total, "задача", "задачи", "задач")} отмечено
            </div>
            {(
              [
                ["базовый", view.content.practice.base, "task"],
                ["применение", view.content.practice.apply, "task"],
                ["дополнительно · вне гейта", view.content.practice.extra, "extra"],
              ] as const
            ).map(([level, tasks, kind]) => {
              if (tasks.length === 0) return null;
              const set = new Set(kind === "extra" ? view.ticks.extra : view.ticks.tasks);
              const done = tasksDone(tasks, set);
              return (
                <div key={level} className="flex flex-col gap-2">
                  <div className="flex font-mono text-[10.5px] uppercase tracking-[.06em] text-mut">
                    <span className="flex-1">{level}</span>
                    <span className={done === tasks.length ? "text-ok" : ""}>
                      {done} / {tasks.length}
                    </span>
                  </div>
                  {tasks.map((t) => (
                    <div key={t.id} id={`${kind}-${t.id}`} className={`rounded-md border bg-card px-3 ${kind === "extra" ? "border-dashed border-bd2" : "border-bd"}`}>
                      <CheckRow checked={set.has(t.id)} onChange={(on) => tick({ kind, key: t.id, on })} label={<span className="text-pretty">{t.text}</span>} />
                    </div>
                  ))}
                </div>
              );
            })}
          </>
        )}

        {tab === "stuck" && (
          <div className="rounded-md border border-bd bg-card p-3" style={{ borderLeft: "2px solid var(--acc)" }} data-testid="stuck">
            <StuckBody stuck={view.content.stuck} />
          </div>
        )}

        {tab === "gate" && <GateCard view={view} onSubmit={onSubmit} pending={pending} />}
      </div>

      <div className="fixed inset-x-0 bottom-[49px] z-10 border-t border-bd bg-bg2 px-4 pb-3 pt-3" data-testid="step-footer">
        <div className="mb-2.5 flex items-center gap-2.5">
          <span className="font-mono text-[10.5px] text-mut">до гейта</span>
          <div className="h-[5px] flex-1 overflow-hidden rounded-[3px] bg-card2" role="progressbar" aria-valuenow={g.units_done} aria-valuemax={g.units_total} aria-label="юниты">
            <div className="h-full bg-acc" style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-[10.5px] text-ink2">
            {g.units_done} / {g.units_total} юнитов
          </span>
        </div>
        <button type="button" onClick={continueStep} className="w-full cursor-pointer rounded-[5px] border border-acc bg-acc py-3 font-mono text-[13px] text-accink">
          продолжить шаг
        </button>
      </div>
      <TabBar />
    </div>
  );
}
